import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import moment from 'moment';
import { useI18n, usePlanLimits } from '@/composables';
import { useTableQuery } from '@/composables/useTableQuery';
import { usePickerPushUrl } from '@/composables/usePickerPushUrl';
import { usePeriodPickerRange } from '@/composables/usePeriodPickerRange';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import query from '@/components/query/query-constructor';
import { UiDatePickerDate } from '@/components/ui/ui-date-picker.vue';
import { supportsCompareReport } from '@/views/reports/report-route-behavior';
import { normalizeDateTimeQueryValue } from '@/views/reports/normalize-datetime-query-value';

interface TimeRange {
  from: string;
  to: string;
  timezone?: string;
  date: string;
}

export interface UsePeriodPickerOptions {
  showCompare?: boolean;
  format?: string;
}

export const usePeriodPicker = ({
  showCompare = true,
  format = 'YYYY-MM-DD HH:mm:ss',
}: UsePeriodPickerOptions = {}) => {
  const route = useRoute();
  const { mdAndUp } = useDisplay();
  const { t } = useI18n();
  const store = useStore();
  const compareReportPlanLimits = usePlanLimits('compareReport');
  const tableQueryApi = useTableQuery();
  const { pushReportUrl } = usePickerPushUrl();
  const periodPickerRange = usePeriodPickerRange(format);

  const picker = ref<{ toggleMenu?: (open: boolean) => void } | null>(null);
  const datePicker = ref<{ adjustPageRange?: () => void } | null>(null);

  const drag = ref<UiDatePickerDate>(null);
  const activeCalendar = ref<'date1' | 'date2'>('date1');
  const compareEnabled = ref(false);

  const rangeDate1 = ref<TimeRange>({
    from: '',
    to: '',
    date: '',
    timezone: '',
  });

  const rangeDate2 = ref<TimeRange>({
    from: '',
    to: '',
    date: '',
  });

  const planLimits = computed(() => 'compareReport');

  const showPlanLimitsIcon = computed(() => compareReportPlanLimits.showPlanLimitsIcon.value);

  const baseItems = periodPickerRange.baseItems;

  const getRangeValueCollection = (args: { timezone: string }) =>
    periodPickerRange.getRangeValueCollection(args);

  const tableQueryForCurrentTab = computed(() => tableQueryApi.tableQueryForCurrentTab.value);

  const isDashboard = computed(
    () => route.name === 'app.blank' || route.name === 'app.index',
  );

  const isFullWidth = mdAndUp;

  const pickerColumns = computed(() => (isFullWidth.value ? 2 : 1));

  const activeDateRange = computed({
    get: () => (activeCalendar.value === 'date2' ? rangeDate2.value : rangeDate1.value),
    set: (value: TimeRange) => {
      if (activeCalendar.value === 'date2') {
        rangeDate2.value = value;
      } else {
        rangeDate1.value = value;
      }
    },
  });

  const getTimeFormatted = (num: number) => (num < 10 ? `0${num}` : `${num}`);

  const times = Array(25)
    .fill(null)
    .map((_, index) => `${getTimeFormatted(index)}:00`);

  const splitTime = (time: string) => {
    const [hoursPart, minutesPart] = time.split(':');
    const offset = +minutesPart <= 30 ? 0 : 1;
    return +hoursPart + offset;
  };

  const isSameDay = computed(() =>
    moment(activeDateRange.value.from).isSame(activeDateRange.value.to, 'day'),
  );

  const isStartAfterEndTime = computed(() =>
    moment(activeDateRange.value.from).isAfter(moment(activeDateRange.value.to)),
  );

  const activeDateRangeForDatePicker = computed({
    get: (): UiDatePickerDate => ({
      start: moment(activeDateRange.value.from).toDate(),
      end: moment(activeDateRange.value.to).toDate(),
      timezone: activeDateRange.value.timezone,
    }),
    set: (value: UiDatePickerDate) => {
      const from = moment(activeDateRange.value.from);
      const to = moment(activeDateRange.value.to);

      activeDateRange.value = {
        ...activeDateRange.value,
        from: moment(value.start)
          .hours(from.hours())
          .minutes(from.minutes())
          .seconds(from.seconds())
          .format(format),
        to: moment(value.end)
          .hours(to.hours())
          .minutes(to.minutes())
          .seconds(to.seconds())
          .format(format),
      };
    },
  });

  const selectHoursItemsStart = computed(() => times.slice(0, times.length - 1));

  const selectHoursItemsEnd = computed(() => {
    const startTime = splitTime(timeFrom.value);
    if (isSameDay.value) {
      return times.slice(1).filter((time) => splitTime(time) > startTime);
    }
    return times.slice(1, times.length);
  });

  const getDateWithUpdatedTime = (date: string, time: string) => {
    const dateWithoutTime = moment(date).format('YYYY-MM-DD');
    return `${dateWithoutTime} ${time}`;
  };

  const dateFrom = computed(() => moment(activeDateRange.value.from).format('DD MMM YYYY'));

  const dateTo = computed(() => moment(activeDateRange.value.to).format('DD MMM YYYY'));

  const timeFrom = computed({
    get: () => moment(activeDateRange.value.from).format('HH:mm'),
    set: (value: string) => {
      const date = getDateWithUpdatedTime(activeDateRange.value.from, value);

      activeDateRange.value = {
        ...activeDateRange.value,
        from: `${date}:00`,
      };

      if (isStartAfterEndTime.value) {
        timeTo.value = '24:00';
      }
    },
  });

  const timeTo = computed({
    get: () => {
      const time = moment(activeDateRange.value.to).add(1, 'seconds').format('HH');
      return time === '00' ? '24:00' : `${time}:00`;
    },
    set: (value: string) => {
      activeDateRange.value = {
        ...activeDateRange.value,
        to: moment(getDateWithUpdatedTime(activeDateRange.value.to, value))
          .subtract(1, 'seconds')
          .format('YYYY-MM-DD HH:mm:ss'),
      };
    },
  });

  const textButtonTimeTo = (range: TimeRange) => {
    const time = moment(range.to).add(1, 'seconds').format('HH');
    return time === '00' ? '24:00' : `${time}:00`;
  };

  const textButtonDate1 = computed(() => {
    if (rangeDate1.value.date === 'customDateTime') {
      return `${moment(rangeDate1.value.from).format('DD MMM YYYY HH:mm')}  - ${moment(rangeDate1.value.to).format('DD MMM YYYY')} ${textButtonTimeTo(rangeDate1.value)}`;
    }
    const item =
      baseItems.value.find((baseItem) => baseItem.value === rangeDate1.value.date) ||
      baseItems.value[0];
    return item.text;
  });

  const textButtonDate2 = computed(() => {
    if (rangeDate2.value.date === 'customDateTime') {
      return `${moment(rangeDate2.value.from).format('DD MMM YYYY HH:mm')}  - ${moment(rangeDate2.value.to).format('DD MMM YYYY')} ${textButtonTimeTo(rangeDate2.value)}`;
    }
    const item =
      baseItems.value.find((baseItem) => baseItem.value === rangeDate2.value.date) ||
      baseItems.value[0];
    return item.text;
  });

  const formatedDate = computed(() => {
    if (rangeDate1.value.date === 'customDateTime') {
      return `${moment(rangeDate1.value.from).format('DD MMM YYYY')} - ${moment(rangeDate1.value.to).format('DD MMM YYYY')}`;
    }
    const item =
      baseItems.value.find((baseItem) => baseItem.value === rangeDate1.value.date) ||
      baseItems.value[0];
    return item.text;
  });

  const showCompareOnThisPage = computed(() => supportsCompareReport(route.name));

  const adjustDatePickerPageRange = () => {
    datePicker.value?.adjustPageRange?.();
  };

  const timeChange = () => {
    activeDateRange.value = {
      ...activeDateRange.value,
      date: 'customDateTime',
    };
  };

  const items = computed((): Bemob.ListItem[] =>
    baseItems.value.map((baseItem) => ({
      key: baseItem.value,
      title: baseItem.text,
      help: baseItem.help,
      click: () => {
        const rangeCollection = getRangeValueCollection({ timezone: rangeDate1.value.timezone });
        activeDateRange.value = {
          ...activeDateRange.value,
          ...(rangeCollection[baseItem.value as keyof typeof rangeCollection] || {}),
          date: baseItem.value,
        };
        nextTick(() => {
          adjustDatePickerPageRange();
        });
      },
    })),
  );

  const closeMenu = () => {
    picker.value?.toggleMenu?.(false);
  };

  const getUpdatedRange = (val: { to: string; from: string }) => {
    const normalizedFrom = normalizeDateTimeQueryValue('from', val.from) as string | undefined;
    const normalizedTo = normalizeDateTimeQueryValue('to', val.to) as string | undefined;

    return {
      ...val,
      from: normalizedFrom ?? val.from,
      to: normalizedTo ?? val.to,
    };
  };

  const saveRange = ({ isPushUrl }: { isPushUrl: boolean }) => {
    const updatedRangeDate1 = getUpdatedRange(rangeDate1.value);
    const updatedRangeDate2 = getUpdatedRange(rangeDate2.value);

    const isRange1Changed =
      updatedRangeDate1.from !== tableQueryForCurrentTab.value.from ||
      updatedRangeDate1.to !== tableQueryForCurrentTab.value.to ||
      updatedRangeDate1?.timezone !== tableQueryForCurrentTab.value.timezone ||
      rangeDate1.value?.date !== tableQueryForCurrentTab.value.date;

    const isRange2Changed =
      updatedRangeDate2.from !== tableQueryForCurrentTab.value.compareFrom ||
      updatedRangeDate2.to !== tableQueryForCurrentTab.value.compareTo ||
      rangeDate2.value.date !== tableQueryForCurrentTab.value.compareDate ||
      compareEnabled.value !== tableQueryForCurrentTab.value.compareEnabled;

    if (isDashboard.value) {
      store.patchDashboardQueryPeriod({
        from: updatedRangeDate1.from,
        to: updatedRangeDate1.to,
        timezone: updatedRangeDate1.timezone,
        date: rangeDate1.value.date,
      });
    } else {
      tableQueryApi.patchTablePeriodForCurrentTab({
        from: updatedRangeDate1.from,
        to: updatedRangeDate1.to,
        timezone: updatedRangeDate1.timezone,
        date: rangeDate1.value.date,
        compareFrom: updatedRangeDate2.from,
        compareTo: updatedRangeDate2.to,
        compareDate: rangeDate2.value.date,
        compareEnabled: compareEnabled.value,
      });
    }

    if (showCompare ? isRange1Changed || isRange2Changed : isRange1Changed) {
      $emitter.emit('reset-offset');
      if (isPushUrl) {
        nextTick(() => {
          pushReportUrl();
        });
      }
    }
  };

  const resolveDateKey = (value: unknown) => {
    if (typeof value === 'string' && value) return value;
    return 'today';
  };

  const applyPresetToRange = (
    range: TimeRange,
    rangeCollection: ReturnType<typeof getRangeValueCollection>,
    fallbackFrom?: string,
    fallbackTo?: string,
  ): TimeRange => {
    if (range.date === 'customDateTime') {
      return range;
    }

    const presetRange = rangeCollection[range.date as keyof typeof rangeCollection];
    if (presetRange) {
      return {
        ...range,
        from: presetRange.from,
        to: presetRange.to,
      };
    }

    if (fallbackFrom && fallbackTo) {
      return {
        ...range,
        date: 'customDateTime',
        from: fallbackFrom,
        to: fallbackTo,
      };
    }

    return {
      ...range,
      date: 'today',
      from: rangeCollection.today.from,
      to: rangeCollection.today.to,
    };
  };

  const applyDataFrom = ({ fromUrl }: { fromUrl: boolean }) => {
    let dataSource: Record<string, unknown> = isDashboard.value
      ? { ...store.dashboardQueryTemporary }
      : { ...tableQueryForCurrentTab.value };

    if (fromUrl) {
      const decoded = query.decode(route.params.data as string);
      if (decoded && typeof decoded === 'object') {
        dataSource = { ...(decoded as Record<string, unknown>) };
      }
    }

    const timezone = (dataSource.timezone as string | undefined) ?? store.user.defaultTimezone;
    const date = resolveDateKey(dataSource.date);
    const from = normalizeDateTimeQueryValue('from', dataSource.from) as string | undefined;
    const to = normalizeDateTimeQueryValue('to', dataSource.to) as string | undefined;

    rangeDate1.value = applyPresetToRange(
      {
        from: from ?? '',
        to: to ?? '',
        timezone,
        date,
      },
      getRangeValueCollection({ timezone }),
      from,
      to,
    );

    const rangeCollection = getRangeValueCollection({ timezone: rangeDate1.value.timezone });

    if (showCompare) {
      const compareEnabledFromSource = Boolean(dataSource.compareEnabled);
      const compareFrom = normalizeDateTimeQueryValue(
        'compareFrom',
        dataSource.compareFrom,
      ) as string | null | undefined;
      const compareTo = normalizeDateTimeQueryValue(
        'compareTo',
        dataSource.compareTo,
      ) as string | null | undefined;
      const compareDateRaw = dataSource.compareDate;
      const compareDate =
        typeof compareDateRaw === 'string' && compareDateRaw ? compareDateRaw : '';

      if (compareDate) {
        rangeDate2.value = applyPresetToRange(
          {
            from: compareFrom ?? '',
            to: compareTo ?? '',
            date: compareDate,
            timezone,
          },
          rangeCollection,
          compareFrom ?? undefined,
          compareTo ?? undefined,
        );
      } else {
        rangeDate2.value = {
          from: compareFrom ?? '',
          to: compareTo ?? '',
          date: '',
        };
      }

      compareEnabled.value = compareEnabledFromSource;
    }
  };

  const calcSimilarCompareRange = () => {
    const rangeCollection = getRangeValueCollection({ timezone: rangeDate1.value.timezone });
    const dateForDate2: Record<string, string> = {
      today: 'yesterday',
      yesterday: 'customDateTime',
      last3days: 'customDateTime',
      last7days: 'customDateTime',
      last30days: 'customDateTime',
      last24hours: 'customDateTime',
      last48hours: 'customDateTime',
      thisMonth: 'lastMonth',
      lastMonth: 'customDateTime',
      maxAvailable: 'thisMonth',
      customDateTime: 'thisMonth',
    };

    if (['today', 'yesterday', 'last3days', 'last7days', 'last30days'].includes(rangeDate1.value.date)) {
      const diff = moment(rangeDate1.value.to).diff(moment(rangeDate1.value.from), 'days');
      rangeDate2.value = {
        from: moment(rangeDate1.value.from)
          .startOf('day')
          .subtract(diff + 1, 'days')
          .format(format),
        to: moment(rangeDate1.value.to)
          .endOf('day')
          .subtract(diff + 1, 'days')
          .format(format),
        date: dateForDate2[rangeDate1.value.date],
      };
      return;
    }

    if (rangeDate1.value.date === 'last24hours') {
      rangeDate2.value = {
        from: moment()
          .tz(rangeDate1.value.timezone)
          .startOf('hour')
          .subtract(23 + 24, 'hours')
          .format(format),
        to: moment()
          .tz(rangeDate1.value.timezone)
          .startOf('hour')
          .subtract(24 - 1, 'hours')
          .format(format),
        date: dateForDate2[rangeDate1.value.date],
      };
      return;
    }

    if (rangeDate1.value.date === 'last48hours') {
      rangeDate2.value = {
        from: moment()
          .tz(rangeDate1.value.timezone)
          .startOf('hour')
          .subtract(48 + 47, 'hours')
          .format(format),
        to: moment()
          .tz(rangeDate1.value.timezone)
          .startOf('hour')
          .subtract(48 - 1, 'hours')
          .format(format),
        date: dateForDate2[rangeDate1.value.date],
      };
      return;
    }

    if (rangeDate1.value.date === 'thisMonth') {
      rangeDate2.value = {
        ...rangeCollection.lastMonth,
        date: dateForDate2[rangeDate1.value.date],
      };
      return;
    }

    if (rangeDate1.value.date === 'lastMonth') {
      rangeDate2.value = {
        from: moment().subtract(2, 'months').startOf('month').format(format),
        to: moment().subtract(2, 'months').endOf('month').format(format),
        date: dateForDate2[rangeDate1.value.date],
      };
      return;
    }

    if (rangeDate1.value.date === 'customDateTime' || rangeDate1.value.date === 'maxAvailable') {
      rangeDate2.value = {
        ...rangeCollection.thisMonth,
        date: dateForDate2[rangeDate1.value.date],
      };
    }
  };

  const toggleCompareReport = (value: boolean) => {
    compareEnabled.value = value;
    calcSimilarCompareRange();
    tableQueryApi.patchTableQueryTemporaryField({
      name: 'compareEnabled',
      value,
    });
  };

  const setActiveRange = (calendar: 'date1' | 'date2') => {
    activeCalendar.value = calendar;
    nextTick(() => {
      adjustDatePickerPageRange();
    });
  };

  const handleDrag = (value: UiDatePickerDate) => {
    if (value) {
      drag.value = value;
      return;
    }
    if (!value && !!drag.value) {
      activeDateRange.value = {
        ...activeDateRange.value,
        date: 'customDateTime',
      };

      drag.value = null;
    }
  };

  const resetRange = () => {
    closeMenu();
    applyDataFrom({ fromUrl: false });
    if (!isDashboard.value) {
      $emitter.emit('reload-report');
    }
  };

  const applyRange = () => {
    closeMenu();
    saveRange({ isPushUrl: true });

    nextTick(() => {
      if (isDashboard.value) {
        $emitter.emit('refresh-dashboard');
      } else {
        $emitter.emit('refresh-report');
      }
    });
  };

  const onMenuClick = () => {
    activeCalendar.value = 'date1';
  };

  const onTimezoneChange = (timezone: string) => {
    rangeDate1.value.timezone = timezone;
    tableQueryApi.patchTablePeriodTemporaryForCurrentTab({
      from: rangeDate1.value.from,
      to: rangeDate1.value.to,
      timezone,
      date: rangeDate1.value.date,
      compareFrom: rangeDate2.value.from,
      compareTo: rangeDate2.value.to,
      compareDate: rangeDate2.value.date,
      compareEnabled: compareEnabled.value,
    });
  };

  const onBusApplyFilters = () => {
    saveRange({ isPushUrl: true });
  };

  const onBusReportTabAdded = () => {
    nextTick(() => {
      applyDataFrom({ fromUrl: true });
      saveRange({ isPushUrl: false });
    });
  };

  const onBusReportTabUpdated = () => {
    nextTick(() => {
      applyDataFrom({ fromUrl: true });
      saveRange({ isPushUrl: false });
    });
  };

  watch(isFullWidth, () => {
    adjustDatePickerPageRange();
  });

  watch(
    activeDateRange,
    () => {
      const updatedRange1 = getUpdatedRange(rangeDate1.value);
      const updatedRange2 = getUpdatedRange(rangeDate2.value);

      if (isDashboard.value) {
        const dashboardTemporary = store.dashboardQueryTemporary;
        const nextPeriod = {
          from: updatedRange1.from,
          to: updatedRange1.to,
          timezone: rangeDate1.value.timezone,
          date: rangeDate1.value.date,
        };
        if (
          dashboardTemporary.from === nextPeriod.from &&
          dashboardTemporary.to === nextPeriod.to &&
          dashboardTemporary.timezone === nextPeriod.timezone &&
          dashboardTemporary.date === nextPeriod.date
        ) {
          return;
        }
        store.patchDashboardQueryPeriodTemporary(nextPeriod);
      } else {
        tableQueryApi.patchTablePeriodTemporaryForCurrentTab({
          from: updatedRange1.from,
          to: updatedRange1.to,
          timezone: rangeDate1.value.timezone,
          date: rangeDate1.value.date,
          compareFrom: updatedRange2.from,
          compareTo: updatedRange2.to,
          compareDate: rangeDate2.value.date,
          compareEnabled: compareEnabled.value,
        });
      }
    },
    { deep: true },
  );

  onMounted(() => {
    $emitter.on('apply-filters', onBusApplyFilters);
    $emitter.on('report-tab-added', onBusReportTabAdded);
    $emitter.on('report-tab-updated', onBusReportTabUpdated);

    if (isDashboard.value) {
      applyDataFrom({ fromUrl: true });
      saveRange({ isPushUrl: true });
    }
  });

  onBeforeUnmount(() => {
    $emitter.off('apply-filters', onBusApplyFilters);
    $emitter.off('report-tab-added', onBusReportTabAdded);
    $emitter.off('report-tab-updated', onBusReportTabUpdated);
  });

  return {
    t,
    timezoneList: store.timezoneList,
    showCompare,
    planLimits,
    showPlanLimitsIcon,
    showCompareOnThisPage,
    compareEnabled,
    activeCalendar,
    activeDateRange,
    textButtonDate1,
    textButtonDate2,
    items,
    activeDateRangeForDatePicker,
    pickerColumns,
    dateFrom,
    dateTo,
    timeFrom,
    timeTo,
    rangeDate1,
    selectHoursItemsStart,
    selectHoursItemsEnd,
    formatedDate,
    toggleCompareReport,
    calcSimilarCompareRange,
    setActiveRange,
    handleDrag,
    resetRange,
    applyRange,
    onMenuClick,
    onTimezoneChange,
    timeChange,
    picker,
    datePicker,
  };
};
